// src/pages/Menu.jsx
// ✅ 1) Remove fritta items (keep only real pizzas)
// ✅ 2) Sort pizzas by jerseyNumber (NOT id), ascending
// ✅ 3) Use jerseyNumber badge on the card instead of id
// ✅ Content adapted: more “football squad / profiles / lineup” vibe + clearer copy

import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Flame, Leaf, ArrowDown } from "lucide-react";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

import { D10, SECTIONS, PIZZAS } from "../data/menu";

const SITE = {
  name: "D10's Pizza Fantasista",
  baseUrl: "https://www.d10s.pizza",
  ogDefault: "/og-preview.png",
};

/* ---------- helpers ---------- */
const jerseyToNumber = (val) => {
  const n = Number(String(val ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(n) ? n : 9999;
};

const firstSentence = (txt) => {
  if (!txt || typeof txt !== "string") return "";
  const cleaned = txt.replace(/\s+/g, " ").trim();
  if (!cleaned) return "";
  const idx = cleaned.indexOf(".");
  if (idx === -1) return cleaned;
  return cleaned.slice(0, idx + 1);
};

/* ---------- 1. HERO SECTION ---------- */
const MenuHero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <header
      ref={containerRef}
      className="relative w-full h-[62vh] min-h-[480px] overflow-hidden bg-[#0F172A] flex items-center justify-center"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src="/img/squad.jpg"
          alt="D10 menu background"
          className="w-full h-full object-cover opacity-60"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent" />
        <div className="absolute inset-0 bg-[#0087E6]/10 mix-blend-multiply" />
      </motion.div>

      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto mt-12">
        <motion.div style={{ y: textY }}>
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="bg-[#0087E6] text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-sm">
              Season 2025/26
            </span>
            <span className="bg-white/10 text-white px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-sm border border-white/10">
              Prosika, Pag
            </span>
          </div>

          <h1 className="font-anton text-6xl sm:text-8xl md:text-9xl text-white uppercase leading-[0.85] mb-6 drop-shadow-2xl">
            The Squad
          </h1>

          <p className="font-serif italic text-white/90 text-lg sm:text-2xl max-w-2xl mx-auto mb-8 leading-snug">
            “Every pizza has a number, a profile, and a position on the pitch.”
          </p>

          <p className="font-manrope text-white/70 text-sm sm:text-base max-w-xl mx-auto mb-10">
            Pick your #. Tap the card. Read the profile. Then do the only logical thing.
          </p>

          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="#pizze"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#0087E6] text-white font-bold uppercase text-xs tracking-widest hover:bg-white hover:text-[#0F172A] transition-all duration-300 rounded-full shadow-lg"
            >
              View Lineup <ArrowDown size={14} />
            </a>

            <a
              href={D10?.instagram || "https://www.instagram.com/d10spizza/"}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 text-white font-bold uppercase text-xs tracking-widest hover:bg-white hover:text-[#0F172A] transition-all duration-300 rounded-full border border-white/10"
            >
              Matchday pics
            </a>
          </div>
        </motion.div>
      </div>
    </header>
  );
};

/* ---------- 2. STICKY NAV ---------- */
const CategoryNav = ({ sections }) => {
  const [active, setActive] = useState(sections?.[0]?.key || "");

  useEffect(() => {
    if (!sections?.length) return;

    const handleScroll = () => {
      const offset = 180;
      let current = active;

      sections.forEach((s) => {
        const el = document.getElementById(s.key);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= offset && rect.bottom >= offset) current = s.key;
      });

      if (current !== active) setActive(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections, active]);

  if (!sections?.length) return null;

  return (
    <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 py-0 shadow-sm">
      <div className="container mx-auto px-4 overflow-x-auto no-scrollbar">
        <div className="flex justify-start sm:justify-center gap-8 min-w-max h-16 items-center">
          {sections.map((s) => (
            <a
              key={s.key}
              href={`#${s.key}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(s.key);
                if (el) {
                  const y = el.getBoundingClientRect().top + window.scrollY - 100;
                  window.scrollTo({ top: y, behavior: "smooth" });
                }
                setActive(s.key);
              }}
              className={`relative h-full flex items-center text-xs font-black uppercase tracking-widest transition-colors duration-300 ${
                active === s.key ? "text-[#0087E6]" : "text-gray-400 hover:text-[#0F172A]"
              }`}
              aria-label={`Jump to ${s.title}`}
            >
              {s.title}
              {active === s.key && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-1 bg-[#0087E6]"
                />
              )}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------- 3. CARD ---------- */
const MenuItemCard = ({ item, index }) => {
  const isVegetarian = item.tags?.includes("Vegetarian");
  const isSpicy = item.tags?.includes("Spicy");

  // tighter, squad-style excerpt (not a full paragraph)
  const description =
    item.shortDescription ||
    firstSentence(item.description) ||
    "Ruota di carro style. Big slice, clean flavours, zero drama.";

  // ✅ jersey number is the truth now
  const jersey = item.jerseyNumber;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: index * 0.05, duration: 0.5 }}
      className="h-full"
    >
      <Link
        to={`/menu/${item.slug}`}
        className="group flex flex-col h-full bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#0087E6]/30 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
        aria-label={`Open ${item.name}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          <img
            src={item.heroImage || item.image}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />

          {/* ✅ Top Left Badge: Jersey Number */}
          {jersey != null && (
            <div
              className="absolute top-3 left-3 text-white w-9 h-9 flex items-center justify-center font-anton text-base rounded-md shadow-lg z-10"
              style={{ backgroundColor: item.jerseyColor || "#0F172A" }}
              title="Jersey number"
            >
              {jersey}
            </div>
          )}

          {item.price && (
            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur text-[#0F172A] px-3 py-1 font-anton text-lg rounded-md shadow-lg z-10">
              {item.price}
            </div>
          )}

          <div className="absolute inset-0 bg-[#0087E6]/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <span className="text-white font-bold uppercase tracking-widest border-2 border-white px-6 py-2 rounded-full transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
              View Profile
            </span>
          </div>
        </div>

        <div className="p-6 flex flex-col flex-grow">
          <div className="mb-4">
            {item.nickname && (
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#0087E6] mb-1">
                {item.nickname}
              </div>
            )}
            <h3 className="font-anton text-2xl sm:text-3xl text-[#0F172A] uppercase leading-none group-hover:text-[#0087E6] transition-colors">
              {item.name}
            </h3>
          </div>

          <p className="font-manrope text-sm text-gray-500 leading-relaxed mb-6 flex-grow">
            {description}
          </p>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100 mt-auto">
            <div className="flex items-center gap-2">
              {isVegetarian && (
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-green-700 bg-green-50 px-2 py-1 rounded">
                  <Leaf size={12} /> Veg
                </div>
              )}
              {isSpicy && (
                <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-red-600 bg-red-50 px-2 py-1 rounded">
                  <Flame size={12} /> Spicy
                </div>
              )}
              {!isVegetarian && !isSpicy && (
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  {item.style || "Ruota di carro"}
                </div>
              )}
            </div>

            <div className="text-[10px] font-black uppercase tracking-widest text-[#0F172A]/60">
              Tap for details
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ---------- 4. SECTION ---------- */
const MenuSection = ({ id, sectionData, items }) => {
  if (!items?.length) return null;

  // adapt content: “Items Available” -> “Players Available”
  const countLabel = items.length === 1 ? "Pizzas" : "Pizzas";

  return (
    <section
      id={id}
      className="py-16 sm:py-24 border-b border-gray-100 last:border-0 scroll-mt-20"
      aria-label={sectionData?.title || id}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="text-[#0087E6] font-bold text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-3 block">
            {items.length} {countLabel} Available
          </span>
          <h2 className="font-anton text-5xl sm:text-7xl text-[#0F172A] uppercase mb-4 leading-none">
            {sectionData.title}
          </h2>
          <p className="font-serif italic text-gray-500 text-lg sm:text-xl">
            “{sectionData.description}”
          </p>

          {/* extra helper line to reinforce jerseyNumber logic */}
          <p className="mt-4 font-manrope text-sm text-gray-500">
            Pro tip: choose your number first. Then choose your toppings.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
          {items.map((item, i) => (
            <MenuItemCard key={item.slug || item.id || i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- MAIN ---------- */
export default function MenuPage() {
  const canonical = `${SITE.baseUrl}/menu`;
  const description =
    "Explore the D10 squad: numbered pizza profiles inspired by football icons. Prosika Beach, Pag.";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Menu",
    name: "D10 Pizza Fantasista Menu",
    url: canonical,
    inLanguage: "en",
    provider: {
      "@type": "Restaurant",
      name: SITE.name,
      url: SITE.baseUrl,
      image: `${SITE.baseUrl}${SITE.ogDefault}`,
      address: {
        "@type": "PostalAddress",
        streetAddress: "Prosika 51A",
        addressLocality: "Pag",
        addressCountry: "HR",
      },
    },
    hasMenuSection: (SECTIONS || [])
      .filter((s) => s.key === "pizze")
      .map((s) => ({
        "@type": "MenuSection",
        name: s.title,
        description: s.description,
      })),
  };

  // ✅ Filter out fritta completely, and sort by jerseyNumber
  const pizzasOnlySorted = useMemo(() => {
    const list = Array.isArray(PIZZAS) ? PIZZAS : [];
    return list
      .filter((p) => p.section === "pizze")
      .sort((a, b) => jerseyToNumber(a.jerseyNumber) - jerseyToNumber(b.jerseyNumber));
  }, []);

  // ✅ Since we remove fritta, also remove those sections from nav + render
  const sectionsNoFritta = useMemo(() => {
    const s = Array.isArray(SECTIONS) ? SECTIONS : [];
    return s.filter((sec) => sec.key === "pizze");
  }, []);

  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden selection:bg-[#0087E6] selection:text-white">
      <SEO
        title={`Menu | ${SITE.name}`}
        description={description}
        image={SITE.ogDefault}
        canonical={canonical}
        type="website"
        jsonLd={jsonLd}
      />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400;1,700&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        html { scroll-behavior: smooth; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <Navbar />

      <MenuHero />

      <CategoryNav sections={sectionsNoFritta} />

      {/* ✅ Only render Pizze, sorted by jersey number */}
      {sectionsNoFritta.map((section) => (
        <MenuSection
          key={section.key}
          id={section.key}
          sectionData={section}
          items={pizzasOnlySorted}
        />
      ))}

      <Footer />
    </div>
  );
}
